import { calculateContent, findWidgetMaxScaleRatio, scaleWidgets } from 'modules/Editor/store/infographSlice.helpers';
import { PageSize, WidgetsMap } from 'types/infographTypes';
import { AllWidgetData, WidgetMap } from 'widgets/Widget.types';

import { THUMBNAIL_OVERLAY_BTN_CLASS } from './TemplatesMenu.config';
import { TemplateSize } from './TemplatesMenu.types';

/**
 * Helper fn to check if the page number in a given template is currently being loaded + added to canvas
 *
 * @param loadingTemplateId template currently being loaded with format `<TEMPLATE_ID>/<PAGE_NUMBER>` - page_number is optional
 * @param currentTemplateId Current template
 * @param pageNumber Page number to check
 * @returns boolean
 */
export const isLoadingPage = (loadingTemplateId: string | null, currentTemplateId: string, pageNumber: number) => {
  if (!loadingTemplateId) return false;

  const [templateId, loadingPageNum] = loadingTemplateId.split('/');
  return templateId === currentTemplateId && loadingPageNum === `${pageNumber}`;
};

/**
 * Blur the thumbnail overlay button if it is active
 */
export const blurTemplateOverlayButtonIfActive = () => {
  const activeElement = document.activeElement as HTMLElement;
  if (activeElement?.classList.contains(THUMBNAIL_OVERLAY_BTN_CLASS)) {
    activeElement.blur();
  }
};

/**
 * Scale widget position and size, then returns the WidgetsMap object.
 *
 * @param widgets Widgets to be scaled
 * @param param1 xRatio, yRatio
 * @param param2 paddingTop, paddingLeft
 * @returns {WidgetsMap}
 */
export const rescaleAndMapTemplates = (
  pageWidgets: WidgetMap,
  size: PageSize,
  templateSize: TemplateSize,
): WidgetsMap => {
  // scale the widgets of the new template to fit the current page size
  const { xRatio: initialXRatio, yRatio: initialYRatio } = calculateContent(size, templateSize);
  const { xRatio, yRatio } = findWidgetMaxScaleRatio(initialXRatio, initialYRatio, pageWidgets);
  const allWidgets = scaleWidgets(pageWidgets, [xRatio, yRatio]);
  if (!allWidgets) return {};

  const rescaledWidgets: WidgetsMap = {};
  allWidgets.forEach((item) => (rescaledWidgets[item.widgetId] = item.widgetData as AllWidgetData));

  return rescaledWidgets;
};
